import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CallStatus } from 'types/auth';
import VideoSessionContext from './VideoSessionContext';
import useCallQueue from 'hooks/call-queue/useCallQueue';
import useApi from 'hooks/useApi';

interface CallData {
  id?: string;
  transaction?: string;
  fromName?: string;
  status?: CallStatus;
  [key: string]: any;
}

interface PersistentCallContextType {
  isCallActive: boolean;
  isCallMinimized: boolean;
  callData: CallData | null;
  setIsCallMinimized: (minimized: boolean) => void;
  maximizeCall: () => void;
  minimizeCall: () => void;
  isOnCallPage: boolean;
}

const PersistentCallContext = createContext<PersistentCallContextType | null>(null);

export const usePersistentCall = () => {
  const context = useContext(PersistentCallContext);
  if (!context) {
    throw new Error('usePersistentCall must be used within a PersistentCallProvider');
  }
  return context;
};

interface PersistentCallProviderProps {
  children: ReactNode;
}

export const PersistentCallProvider: React.FC<PersistentCallProviderProps> = ({ children }) => {
  const [isCallMinimized, setIsCallMinimized] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, showToast } = useApi();
  
  // Track previous session status to detect state changes - must be at top level
  const prevStatus = React.useRef<CallStatus | null>(null);
  
  // Get session data from VideoSessionContext
  const videoSessionContext = useContext(VideoSessionContext);
  const session = videoSessionContext?.session;

  // Get incoming calls from global call queue
  const incomingCalls = useCallQueue(user?.uid || '');

  // Check if currently on a call page
  const isOnCallPage = location.pathname.startsWith('/session/');

  // Find active call from either session or incoming calls
  // Prioritize incoming calls over existing session to ensure new calls are detected
  const activeCall = (incomingCalls && incomingCalls.length > 0 ? incomingCalls[0] : null) || session;

  // Determine if call is active based on session or incoming calls
  const isCallActive = Boolean(
    activeCall && 
    activeCall.status && 
    [CallStatus.IN_PROGRESS, CallStatus.RECEIVED, CallStatus.CALLING, CallStatus.RECEIVING].includes(activeCall.status)
  );

  // (render-loop debug logging removed - it fired on every render)


  // Auto-minimize when navigating away from call page
  useEffect(() => {
    if (isCallActive && !isOnCallPage && !isCallMinimized) {
      setIsCallMinimized(true);
    }
  }, [isCallActive, isOnCallPage, isCallMinimized]);

  // Reset minimized state when call ends
  useEffect(() => {
    if (!isCallActive) {
      setIsCallMinimized(false);
    }
  }, [isCallActive]);

  // Effect to clear session when call ends to allow new calls
  // The provider recreates its value object every render, so keeping the whole
  // context in the dep array below re-ran this effect continuously and stacked
  // up uncancelled timers.
  const videoSessionRef = useRef(videoSessionContext);
  useEffect(() => {
    videoSessionRef.current = videoSessionContext;
  }, [videoSessionContext]);

  // Tear down the Agora session ~1s after a call ends.
  //
  // This used to be unscoped: it cleaned up whatever was current when the timer
  // fired, with no record of which call it was scheduled for. Start the next
  // call inside that window - which is normal, you hang up and immediately dial
  // again - and it would leave the NEW channel, null the NEW client and audio
  // track and clear callUid. That is the "sometimes there is video and audio,
  // sometimes there is not": whether it broke depended purely on whether the
  // stale timer landed before or after the new join.
  useEffect(() => {
    const endedCallId = session?.transaction;
    const ended = session && (
      session.status === CallStatus.COMPLETED ||
      session.status === CallStatus.USER_LEFT ||
      session.status === CallStatus.MANUAL_DISCONNECT ||
      session.status === CallStatus.MISSED_CALL
    );
    if (!ended || !endedCallId) return;

    const timer = setTimeout(() => {
      const ctx = videoSessionRef.current;
      // Moved on to another call already - that one owns the client now.
      if (ctx?.callUid && ctx.callUid !== endedCallId) {
        console.log('PersistentCallContext: skipping cleanup, a newer call is active');
        return;
      }
      (ctx?.forceCleanupSession || ctx?.cleanupSession)?.();
    }, 1000);

    return () => clearTimeout(timer);
  }, [session?.status, session?.transaction]);

  // Effect to handle user disconnection notifications and chat session end
  useEffect(() => {
    if (session && prevStatus.current !== session.status) {
      // Check if user has left the call
      if (session.status === CallStatus.USER_LEFT && 
          prevStatus.current === CallStatus.IN_PROGRESS) {
        
        console.log('PersistentCallContext: User left the call, handling disconnection');
        
        // If currently on the video call screen, show redirecting loader and navigate
        if (isOnCallPage) {
          // Show a toast notification about redirecting
          showToast(
            `${session.sessionType === 'CHAT' ? 'Chat' : 'Call'} ended - User has disconnected. Redirecting...`,
            'info'
          );
          
          // Navigate to home page after a brief delay to show the message
          setTimeout(() => {
            navigate('/');
          }, 2000); // 2 second delay to show the redirecting message
        } else {
          // If on any other route, show notification
          // Using a small delay to ensure any navigation has completed
          setTimeout(() => {
            // Check if we're still not on the call page
            if (!location.pathname.startsWith('/session/')) {
              // Show toast notification that the call has ended
              showToast(
                `${session.sessionType === 'CHAT' ? 'Chat' : 'Call'} ended - User has disconnected`,
                'success'
              );
            }
          }, 100);
        }
      }

      // Check if chat session has ended (COMPLETED status)
      if (session.status === CallStatus.COMPLETED && 
          session.sessionType === 'CHAT' &&
          prevStatus.current === CallStatus.IN_PROGRESS) {
        
        console.log('PersistentCallContext: Chat session completed, handling notification');
        
        // If currently on the chat screen, the ChatWindow component will handle showing the message
        // and redirect. If on any other route, show notification
        if (!isOnCallPage) {
          // Using a small delay to ensure any navigation has completed
          setTimeout(() => {
            // Check if we're still not on the session page
            if (!location.pathname.startsWith('/session/')) {
              // Show toast notification that the chat session has ended
              showToast(
                'Chat session ended',
                'success'
              );
            }
          }, 100);
        }
      }
      
      // Update previous status for next comparison
      prevStatus.current = session.status;
    }
  }, [session?.status, isOnCallPage, location.pathname, showToast, navigate]);

  const maximizeCall = () => {
    setIsCallMinimized(false);
  };

  const minimizeCall = () => {
    setIsCallMinimized(true);
  };

  const value: PersistentCallContextType = {
    isCallActive,
    isCallMinimized,
    callData: activeCall || null,
    setIsCallMinimized,
    maximizeCall,
    minimizeCall,
    isOnCallPage
  };

  return (
    <PersistentCallContext.Provider value={value}>
      {children}
    </PersistentCallContext.Provider>
  );
};
